import { CHESS_SIDE } from "vue-chess-shared";
import { mapGetters, mapActions } from "vuex";
import _ from "lodash";

export const BOARD_SIZES = {
  xs: 280,
  sm: 340,
  md: 440,
  lg: 560,
  xl: 640,
  xxl: 720,
  xxxl: 800,
  max: 1000,
};

const BREAKPOINTS = {
  xs: 360,
  sm: 480,
  md: 768,
  lg: 1024,
};

export const boardSizeMixin = {
  props: {
    isPresentationMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
    };
  },

  computed: {
    ...mapGetters({
      boardSize: "getScenarioBoardSize",
      boardOrientation: "getCoachingOrientation",
    }),

    presentationSize() {
      const vh = Math.min(this.windowHeight, 0.64 * this.windowWidth) - 108;
      return `${vh}px`;
    },

    boardStyle() {
      const size = this.isPresentationMode
        ? this.presentationSize
        : `${this.boardSize}px`;
      document.documentElement.style.setProperty("--board-size", size);

      return {
        width: size,
        height: size,
      };
    },

    movesTableStyle() {
      const height = this.isPresentationMode
        ? this.presentationSize
        : this.boardSize
        ? `${this.boardSize}px`
        : `${BOARD_SIZES.lg}px`;

      return { height };
    },
  },

  methods: {
    ...mapActions(["updateScenarioBoardSize", "updateCoachingOrientation"]),

    triggerResize() {
      this.$nextTick(() => {
        document.body.dispatchEvent(new Event("chessground.resize"));
      });
    },

    handleBoardSizeChange(size) {
      if (!this.isPresentationMode) {
        this.updateScenarioBoardSize(size);
        this.triggerResize();
      }
    },

    flipBoard() {
      const newOrientation =
        this.boardOrientation === CHESS_SIDE.WHITE
          ? CHESS_SIDE.BLACK
          : CHESS_SIDE.WHITE;

      this.updateCoachingOrientation(newOrientation);
    },

    getInitialBoardSize() {
      const width = window.innerWidth;

      if (width >= BREAKPOINTS.lg) return BOARD_SIZES.xl;
      if (width >= BREAKPOINTS.md) return BOARD_SIZES.lg;
      if (width >= BREAKPOINTS.sm) return BOARD_SIZES.md;
      if (width >= BREAKPOINTS.xs) return BOARD_SIZES.sm;
      return BOARD_SIZES.xs;
    },

    initializeBoardSize() {
      if (!this.isPresentationMode && !this.boardSize) {
        const initialSize = this.getInitialBoardSize();
        this.handleBoardSizeChange(initialSize);
      }
    },

    handleResize() {
      if (this.isPresentationMode) {
        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;

        // Force update and trigger resize
        this.$forceUpdate();
        this.triggerResize();
      }
    },
  },

  created() {
    // Create a debounced version of handleResize to prevent too many updates
    this.debouncedHandleResize = _.debounce(this.handleResize, 100);
  },

  mounted() {
    this.initializeBoardSize();
    if (this.isPresentationMode) {
      window.addEventListener("resize", this.debouncedHandleResize);
    }
  },

  beforeDestroy() {
    if (this.isPresentationMode) {
      window.removeEventListener("resize", this.debouncedHandleResize);
    }
  },

  watch: {
    isPresentationMode(newVal) {
      if (newVal) {
        window.addEventListener("resize", this.debouncedHandleResize);
      } else {
        window.removeEventListener("resize", this.debouncedHandleResize);
      }
      this.triggerResize();
    },

    // Watch for window dimension changes and trigger resize in presentation mode
    windowWidth() {
      if (this.isPresentationMode) {
        this.triggerResize();
      }
    },

    windowHeight() {
      if (this.isPresentationMode) {
        this.triggerResize();
      }
    },
  },
};
